// Money is integer base units as decimal strings; bigint in code. Never floats.

const DIGITS = /^\d+$/;

function toBigInt(base: string): bigint {
  const s = base.trim();
  if (!DIGITS.test(s)) throw new Error(`Not an integer base-unit amount: "${base}"`);
  return BigInt(s);
}

/**
 * Exact decimal string for a base-unit amount, "." as the separator, no grouping.
 * Trailing zeros are trimmed down to `minFraction` digits.
 *   formatUnits("25000000", 6) === "25.00"
 *   formatUnits("25001234", 6) === "25.001234"
 */
export function formatUnits(base: string, decimals: number, minFraction = 2): string {
  const value = toBigInt(base);
  if (decimals === 0) return value.toString();
  const scale = 10n ** BigInt(decimals);
  const whole = value / scale;
  let fraction = (value % scale).toString().padStart(decimals, "0");
  const keep = Math.min(minFraction, decimals);
  while (fraction.length > keep && fraction.endsWith("0")) fraction = fraction.slice(0, -1);
  return fraction.length ? `${whole}.${fraction}` : whole.toString();
}

/** Parse a decimal string ("25", "25.5", "25.000001") into base units. Rejects excess precision. */
export function parseUnits(amount: string, decimals: number): string {
  const s = amount.trim();
  const m = /^(\d+)(?:\.(\d*))?$/.exec(s);
  if (!m) throw new Error(`Not a decimal amount: "${amount}"`);
  const whole = m[1] ?? "0";
  const fraction = m[2] ?? "";
  if (fraction.length > decimals) throw new Error(`Too many decimals in "${amount}" (max ${decimals})`);
  const scale = 10n ** BigInt(decimals);
  return (BigInt(whole) * scale + BigInt(fraction.padEnd(decimals, "0") || "0")).toString();
}

interface Separators {
  group: string;
  decimal: string;
}

const separatorCache = new Map<string, Separators>();

function separators(locale: string): Separators {
  const cached = separatorCache.get(locale);
  if (cached) return cached;
  let group = ",";
  let decimal = ".";
  try {
    for (const part of new Intl.NumberFormat(locale).formatToParts(1234567.5)) {
      if (part.type === "group") group = part.value;
      if (part.type === "decimal") decimal = part.value;
    }
  } catch {
    // Unknown locale: keep the defaults.
  }
  const result = { group, decimal };
  separatorCache.set(locale, result);
  return result;
}

/** Split a plain decimal string into its whole and fraction parts, grouping the whole for display. */
export function localizeDecimal(plain: string, locale: string): { whole: string; fraction: string; decimal: string } {
  const { group, decimal } = separators(locale);
  const [whole = "0", fraction = ""] = plain.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, group);
  return { whole: grouped, fraction, decimal };
}

/** Human display of a base-unit amount with locale separators: "1,250.00" (en) / "1 250,00" (sk). */
export function formatDisplay(base: string, decimals: number, locale: string, minFraction = 2): string {
  const { whole, fraction, decimal } = localizeDecimal(formatUnits(base, decimals, minFraction), locale);
  return fraction ? `${whole}${decimal}${fraction}` : whole;
}

export function compareUnits(a: string, b: string): -1 | 0 | 1 {
  const x = toBigInt(a);
  const y = toBigInt(b);
  return x === y ? 0 : x < y ? -1 : 1;
}

/** |a - b| in base units, as a string. */
export function diffUnits(a: string, b: string): string {
  const d = toBigInt(a) - toBigInt(b);
  return (d < 0n ? -d : d).toString();
}

/** Shorten an address or hash for display: "7xKX…9fGh". */
export function shortHash(value: string, head = 4, tail = 4): string {
  if (value.length <= head + tail + 1) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}
